import debounce from 'lodash/debounce';
import { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { spreadsheet as coreSpreadsheet } from '@industriousapps/excelkits-core';
import univerService from '@/services/univerjs';
import { getWorkbookSheets } from '@/services/univerjs/utils';
import globalEvents from '@/utils/globalEvents';
import errors from './errors';
import { calcLeftOffset, calcTopOffset, delayInitialUniverCmd } from './utils';
import { cancelEditing } from '@/services/univerjs/utils';
const {
  coordinatesToAddress,
  addressesToSelection
} = coreSpreadsheet;
const PREFIX = 'components: Spreadsheet: useUniverEvents:';
const UNI_MUTATION_CMDS = 2;
const SUBSCRIBERS = [];
const SHEET_MUTATION_EVENTS = ['sheet.mutation.insert-sheet', 'sheet.mutation.remove-sheet', 'sheet.mutation.set-worksheet-order'];
const SHEET_INSERT_EVENT = SHEET_MUTATION_EVENTS[0];
const SHEET_REMOVE_EVENT = SHEET_MUTATION_EVENTS[1];
const SHEET_REORDER_EVENT = SHEET_MUTATION_EVENTS[2];
export default function useUniverEvents(univerAPI, settings) {
  const univerEventDisposable = useRef(null);
  const activeSheetIdRef = useRef('');
  const updateHash = useRef('');
  const isInitializedRef = useRef(false);
  const {
    0: isSelecting,
    1: setIsSelecting
  } = useState(false);
  const workbookSnapshot = useMemo(() => {
    if (!univerAPI) return null;
    const awb = univerAPI.getActiveWorkbook();
    return (awb === null || awb === void 0 ? void 0 : awb.getSnapshot()) || null;
  }, [univerAPI]);
  const {
    isEditing = true,
    sheetSelections = [],
    sheetScrollOffsets = [],
    onChange = () => null,
    onSelect = () => null,
    onSheetSelect = () => null,
    onScroll = () => null,
    onWarn = () => null,
    onError = () => null
  } = settings; // Emit changes to workbook content
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const triggerWorkbookChange = useCallback(debounce(api => {
    const {
      sum: newUpdateHash
    } = univerService.createUniverWorkbookHash(api);
    if (updateHash.current === newUpdateHash) return;
    updateHash.current = newUpdateHash;
    onChange();
  }, 500, {
    leading: true
  }), [onChange]); // Emit changes to workbook content
  // when editing is cancelled

  useEffect(() => {
    if (!univerAPI) return;
    return globalEvents.subscribe('univerSheetFinalizeChangesStart', async event => {
      var _event$detail;

      // Cancel editing to ensure the latest data is saved
      // and the active cell is defocused
      await cancelEditing(univerAPI);
      globalEvents.trigger('univerSheetFinalizeChangesEnd', {
        detail: {
          // Optional event id to trigger additional actions
          id: (_event$detail = event.detail) === null || _event$detail === void 0 ? void 0 : _event$detail.id,
          // Get the latest workbook data and meta
          workbook: univerService.univerFacadeToWorkbook({
            univerAPI,
            isCompiled: false
          }),
          workbookMeta: univerService.univerFacadeToWorkbookMeta(univerAPI)
        }
      });
    });
  }, [univerAPI]); // Propogate selection change events
  // converting univer selections to cell
  // reference strings

  const handleSelectionChange = useCallback((api, selection) => {
    if (!selection) return;

    try {
      var _workbook$getActiveSh;

      const workbook = api.getActiveWorkbook();
      if (!workbook) throw Error('no active workbook');
      const sheets = getWorkbookSheets(workbook).map(sh => sh.getSheetId());
      const activeSheet = workbook === null || workbook === void 0 ? void 0 : (_workbook$getActiveSh = workbook.getActiveSheet()) === null || _workbook$getActiveSh === void 0 ? void 0 : _workbook$getActiveSh.getSheetId();
      if (!activeSheet) throw Error('no active sheet');
      const sheetIndex = sheets.indexOf(activeSheet);
      const {
        startRow,
        startColumn,
        endRow,
        endColumn
      } = selection;
      const first = coordinatesToAddress(startRow, startColumn, sheetIndex);
      const last = coordinatesToAddress(endRow, endColumn, sheetIndex);
      const result = addressesToSelection({
        first,
        last
      }); // Update sheet selections

      let currentSheetSelection = sheetSelections.find(_ref => {
        let {
          id
        } = _ref;
        return id === activeSheet;
      });

      if (!currentSheetSelection) {
        currentSheetSelection = {
          id: activeSheet,
          selection: ''
        };
        sheetSelections.push(currentSheetSelection);
      }

      currentSheetSelection.selection = result.split('.').pop() || ''; // remove any sheet ref
      // Emit result

      onSelect(result);
    } catch (err) {
      onError(errors.createSelectionError(PREFIX, err));
    }
  }, [onSelect, onError, sheetSelections]); // Emit change of active spreadsheet
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const triggerSheetSelect = useCallback(debounce((api, newSheetId) => {
    const activeWorkbook = api.getActiveWorkbook();
    if (!activeWorkbook) return;

    if (newSheetId !== activeSheetIdRef.current) {
      const wbd = activeWorkbook.getSnapshot();
      onSheetSelect(wbd.sheetOrder.indexOf(newSheetId));
      activeSheetIdRef.current = newSheetId;
    }
  }, 500, {
    leading: true
  }), [onSheetSelect, activeSheetIdRef]); // Undo user updates, after inital render update
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const undoUpdate = useCallback(debounce(api => {
    api === null || api === void 0 ? void 0 : api.executeCommand('univer.command.undo');
    onWarn(errors.createUndoWarning(PREFIX));
  }, 400), [onWarn]); /////////////////////////////////
  // Add Selection Event Listener
  /////////////////////////////////

  useEffect(() => {
    const callback = _ref2 => {
      let {
        api,
        id,
        params = {}
      } = _ref2;

      // On selection change completion
      if (id === 'sheet.operation.set-selections' && // @ts-ignore
      UNI_MUTATION_CMDS === params.type && // @ts-ignore
      Array.isArray(params.selections) && // @ts-ignore
      params.selections[0] && // @ts-ignore
      params.selections[0].range) {
        // @ts-ignore
        handleSelectionChange(api, params.selections[0].range);
      }
    };

    SUBSCRIBERS.push(callback);
    return () => {
      SUBSCRIBERS.splice(SUBSCRIBERS.indexOf(callback), 1);
    };
  }, [handleSelectionChange]); //////////////////////////////
  // Add Change Event Listener
  //////////////////////////////

  useEffect(() => {
    // Emit workbook changes when editing & undos when uneditable
    const callback = _ref3 => {
      let {
        api,
        type,
        id
      } = _ref3;

      if (UNI_MUTATION_CMDS === type && id !== 'formula.mutation.set-formula-data') {
        isEditing ? setTimeout(() => triggerWorkbookChange(api)) : undoUpdate(api);
      }
    };

    SUBSCRIBERS.push(callback);
    return () => {
      SUBSCRIBERS.splice(SUBSCRIBERS.indexOf(callback), 1);
    };
  }, [isEditing, triggerWorkbookChange, undoUpdate]); ////////////////////////////
  // Add Zoom Event Listener
  ////////////////////////////

  useEffect(() => {
    // Prevent/undo zooming when not editing
    const callback = _ref4 => {
      let {
        api,
        id,
        params
      } = _ref4;

      if (!isEditing && id === 'sheet.operation.set-zoom-ratio' && // @ts-ignore
      typeof params.zoomRatio === 'number' && // @ts-ignore
      !isNaN(params.zoomRatio) && // @ts-ignore
      params.zoomRatio !== 1) {
        // TODO: does not currently work, test with later versions of Univer
        api.executeCommand('sheet.command.set-zoom-ratio', {
          // @ts-ignore
          unitId: params.unitId,
          // @ts-ignore
          subUnitId: params.subUnitId,
          zoomRatio: 1
        });
      }
    };

    SUBSCRIBERS.push(callback);
    return () => {
      SUBSCRIBERS.splice(SUBSCRIBERS.indexOf(callback), 1);
    };
  }, [isEditing]); ////////////////////////////////
  // Add Sheet Selection Listener
  ////////////////////////////////

  useEffect(() => {
    const callback = _ref5 => {
      let {
        api,
        id,
        params
      } = _ref5;

      // Active worksheet changes
      if (id === 'sheet.operation.set-worksheet-active' && // @ts-ignore
      typeof params.subUnitId === 'string') {
        triggerSheetSelect(api, params.subUnitId); // Force the view to snap to first row/column
        // so selections do not appear disjointed
        // NOTE: replace with `sheetScrollOffsets` lookup
        //       when Univer sheet scrolling is more
        //       stable, currently too unpredictable

        if (!isEditing) {
          delayInitialUniverCmd(isInitializedRef.current, () => {
            api.executeCommand('sheet.command.scroll-view', {
              sheetViewStartColumn: 0,
              offsetX: 0,
              sheetViewStartRow: 0,
              offsetY: 0
            });
            isInitializedRef.current = true;
          });
        }
      }
    };

    SUBSCRIBERS.push(callback);
    return () => {
      SUBSCRIBERS.splice(SUBSCRIBERS.indexOf(callback), 1);
    };
  }, [triggerSheetSelect, onWarn, isEditing]); ////////////////////////////////////
  // Sheet Add/Remove/Reorder Events
  ////////////////////////////////////

  useEffect(() => {
    const callback = _ref6 => {
      var _params$sheet;

      let {
        id,
        params
      } = _ref6;
      const {
        toOrder
      } = params || {};
      const sheetId = (params === null || params === void 0 ? void 0 : (_params$sheet = params.sheet) === null || _params$sheet === void 0 ? void 0 : _params$sheet.id) || (params === null || params === void 0 ? void 0 : params.subUnitId);
      const sheetOffsets = SHEET_MUTATION_EVENTS.includes(id) ? sheetScrollOffsets.find(_ref7 => {
        let {
          id: sId
        } = _ref7;
        return sId === sheetId;
      }) : null;

      if (id === SHEET_INSERT_EVENT && !sheetOffsets && sheetId) {
        // Add new
        sheetScrollOffsets.push({
          x: 0,
          y: 0,
          id: sheetId
        });
      } else if (id === SHEET_REMOVE_EVENT && sheetOffsets) {
        // Remove
        const index = sheetScrollOffsets.indexOf(sheetOffsets);
        sheetScrollOffsets.splice(index, 1);
      } else if (id === SHEET_REORDER_EVENT && sheetOffsets && typeof toOrder === 'number') {
        // Reorder
        const index = sheetScrollOffsets.indexOf(sheetOffsets);
        const rorderedOffsets = sheetScrollOffsets.splice(index, 1)[0];
        sheetScrollOffsets.splice(toOrder, 0, rorderedOffsets);
      }
    };

    SUBSCRIBERS.push(callback);
    return () => {
      SUBSCRIBERS.splice(SUBSCRIBERS.indexOf(callback), 1);
    };
  }, [sheetScrollOffsets]); ///////////////////////////////
  // Add Scroll Event Listeners
  ///////////////////////////////

  useEffect(() => {
    const callback = _ref8 => {
      let {
        id,
        params = {}
      } = _ref8;
      // @ts-ignore
      const sheetViewStartColumn = parseFloat(params.sheetViewStartColumn); // @ts-ignore

      const offsetX = parseFloat(params.offsetX); // @ts-ignore

      const sheetViewStartRow = parseFloat(params.sheetViewStartRow); // @ts-ignore

      const offsetY = parseFloat(params.offsetY); // Send scroll postion update

      if (id === 'sheet.operation.set-scroll' && !isNaN(sheetViewStartColumn) && !isNaN(offsetX) && !isNaN(sheetViewStartRow) && !isNaN(offsetY)) {
        const sheetOffsets = sheetScrollOffsets.find(_ref9 => {
          let {
            id: sId
          } = _ref9;
          return sId === activeSheetIdRef.current;
        });
        const {
          rowData = {},
          columnData = {}
        } = workbookSnapshot && workbookSnapshot.sheets[activeSheetIdRef.current] || {
          rowData: {},
          columnData: {}
        };

        if (sheetOffsets) {
          const x = sheetViewStartColumn === 0 && offsetX < 0 ? 0 : Math.abs(calcLeftOffset(sheetViewStartColumn, columnData) + offsetX);
          const y = sheetViewStartRow === 0 && offsetY < 0 ? 0 : Math.abs(calcTopOffset(sheetViewStartRow, rowData) + offsetY);
          sheetOffsets.x = x * -1;
          sheetOffsets.y = y * -1;
          onScroll(sheetOffsets);
        }
      }
    };

    SUBSCRIBERS.push(callback);
    return () => {
      SUBSCRIBERS.splice(SUBSCRIBERS.indexOf(callback), 1);
    };
  }, [workbookSnapshot, onScroll, sheetScrollOffsets]); // Setup references

  useEffect(() => {
    // Setup update hash for change comparisions
    if (isEditing && univerAPI && !updateHash.current) {
      updateHash.current = univerService.createUniverWorkbookHash(univerAPI).sum;
    }

    if (univerAPI && !activeSheetIdRef.current) {
      const workbook = univerAPI.getActiveWorkbook();
      const activeSheet = (workbook === null || workbook === void 0 ? void 0 : workbook.getActiveSheet()) || null;

      if (!activeSheet) {
        onError(errors.createUnknownUniverError(PREFIX, new Error('No active sheet found')));
        return;
      }

      activeSheetIdRef.current = activeSheet.getSheetId() || '';
    }

    if (univerAPI && !sheetScrollOffsets.length) {
      const workbook = univerAPI.getActiveWorkbook();

      if (!workbook) {
        onError(errors.createUnknownUniverError(PREFIX, new Error('No active workbook found')));
        return;
      } // Create initial scroll offsets for all sheets


      workbook.getSheets().forEach(sheet => {
        const id = sheet.getSheetId();
        const offsets = sheetScrollOffsets.find(_ref10 => {
          let {
            id: sId
          } = _ref10;
          return sId === id;
        });

        if (!offsets) {
          sheetScrollOffsets.push({
            x: 0,
            y: 0,
            id
          });
        }
      });
    } // NOTE: do not cleanup refs as this is a hot function

  }); // Publish univer events to subscribers

  useEffect(() => {
    if (univerAPI && !univerEventDisposable.current) {
      univerEventDisposable.current = univerAPI.onBeforeCommandExecute(_ref11 => {
        let {
          type,
          id,
          params
        } = _ref11;

        if (typeof type === 'number') {
          SUBSCRIBERS.forEach(sub => sub({
            api: univerAPI,
            type,
            id,
            params
          }));
        }
      });
    }

    return () => {
      var _univerEventDisposabl;

      (_univerEventDisposabl = univerEventDisposable.current) === null || _univerEventDisposabl === void 0 ? void 0 : _univerEventDisposabl.dispose();
      univerEventDisposable.current = null;
    };
  });
  return {
    isSelecting,
    setIsSelecting
  };
}